import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode} from '@angular/common/http';
import {catchError, finalize, map, Observable, shareReplay, switchMap, tap, throwError} from "rxjs";
import {AuthenticationService} from '../../shared/services/authentication.service';
import {HTTP_STATUS_ERROR_CODES_CONSTANTS} from '../constants/http/http-status-error-codes.constants';
import {HTTP_API_ENDPOINTS_CONSTANTS} from '../constants/http/http-endpoints-constants';

@Injectable()
export class OnHttpResponseInterceptor implements HttpInterceptor {

  constructor(
    private authenticationService: AuthenticationService
  ) {
  }

  // NOTE: When 'next.handle()' method get called inside catchError, it will retry the request! .
  // Retried requests go to the next handler only, so a 401 on a retry doesn't trigger another refresh.

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse
          && error.status === HTTP_STATUS_ERROR_CODES_CONSTANTS.UNAUTHORIZED
          && !this.isAuthenticationRequest(request)) {
          return this.handleUnAuthorized401Error(request, next);
        }

        return throwError(() => error);
      })
    );
  }

  // shared by all requests that fail with 401 while a refresh is in progress, so the refresh token is used only once
  private refreshInProgress$: Observable<void> | null = null;

  private handleUnAuthorized401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // another tab (or an earlier refresh) may already have rotated the tokens; then just retry with the new one
    this.authenticationService.syncTokensFromStorage();
    const currentAccessToken = this.authenticationService.accessToken;
    if (currentAccessToken && request.headers.get('Authorization') !== `Bearer ${currentAccessToken}`) {
      return next.handle(this.authenticationService.addInterceptRequestHeaders(request));
    }

    const refreshToken = this.authenticationService.refreshToken;
    if (!refreshToken) {
      this.authenticationService.internalLogout();
      return throwError(() => new Error('Not authenticated'));
    }

    if (!this.refreshInProgress$) {
      this.refreshInProgress$ = this.authenticationService.refreshTokens(refreshToken).pipe(
        tap(data => this.authenticationService.setTokens(data.accessToken, data.refreshToken)),
        map(() => undefined),
        catchError(err => {
          // only logout when the server rejected the refresh token; network/server errors (e.g. cold start)
          // shouldn't kill the session, the next request will retry the refresh.
          if (err instanceof HttpErrorResponse
            && (err.status === HttpStatusCode.Unauthorized || err.status === HttpStatusCode.BadRequest)) {
            this.authenticationService.internalLogout();
          }
          return throwError(() => err);
        }),
        finalize(() => this.refreshInProgress$ = null),
        shareReplay(1)
      );
    }

    return this.refreshInProgress$.pipe(
      switchMap(() => next.handle(this.authenticationService.addInterceptRequestHeaders(request)))
    );
  }

  private isAuthenticationRequest(request: HttpRequest<any>): boolean {
    const authenticationEndpoints = Object.values(HTTP_API_ENDPOINTS_CONSTANTS.AUTHENTICATION);
    return authenticationEndpoints.some(endpoint => request.url.toLowerCase().includes(endpoint.toLowerCase()));
  }

}
